import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { FormControl, FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { AuthService } from '../../../../shared/services/apis/usuario-admin/auth.service';
import { TabelaIbptService } from '../../../../shared/services/apis/motor-fiscal/tabela-ibpt.service';
import { UsuarioLookupService } from '../../../../shared/services/apis/usuario/usuario-lookup.service';
import { UsuarioListaLookupsResponse } from '../../../../shared/services/responses/usuario/usuario-lista-lookups.response';
import { UnidadeFederativa } from '../../../../shared/services/models/usuario/unidade-federativa.model';
import { SelectPesquisavelComponent } from '../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { IbptComparacaoVersaoEnum } from '../../../../shared/enums/motor-fiscal/ibpt-comparacao-versao.enum';
import { ArquivoPlanilhaIbptStruct } from '../../../../shared/services/structs/motor-fiscal/arquivo-planilha-ibpt.struct';
import { LinhaRejeitadaIbptStruct } from '../../../../shared/services/structs/motor-fiscal/linha-rejeitada-ibpt.struct';
import { ResultadoUfImportacaoIbptStruct } from '../../../../shared/services/structs/motor-fiscal/resultado-uf-importacao-ibpt.struct';
import { ImportacaoTabelaIbptRequest } from '../../../../shared/services/requests/motor-fiscal/importacao-tabela-ibpt.request';
import {
  ValidacaoImportacaoTabelaIbptResponse,
} from '../../../../shared/services/responses/motor-fiscal/validacao-importacao-tabela-ibpt.response';
import {
  AplicacaoImportacaoTabelaIbptResponse,
} from '../../../../shared/services/responses/motor-fiscal/aplicacao-importacao-tabela-ibpt.response';
import { MapaUfIbptComponent } from '../mapa-uf-ibpt/mapa-uf-ibpt.component';

export interface ImportarPlanilhaIbptDialogResult {
  // Verdadeiro quando ao menos uma importação foi gravada enquanto o diálogo esteve aberto.
  isConcluido: boolean;
  isVerHistorico: boolean;
}

enum EtapaImportacao {
  EnviarArquivos = 1,
  Conferir = 2,
  Concluir = 3,
}

enum StatusArquivoImportacao {
  Lendo = 1,
  Valido = 2,
  Recusado = 3,
}

// Arquivo da etapa 1, lido no navegador só para mostrar versão, contagem e conflitos. A validação das linhas é da API.
type ArquivoImportacao = {
  id: number;
  arquivo: File;
  nomeArquivo: string;
  idStatus: StatusArquivoImportacao;
  motivoRecusa: string | null;
  // UF escolhida no select (id de db_usuario.unidade_federativa); sugerida pelo nome do arquivo.
  idUnidadeFederativaControl: FormControl<number | null>;
  versao: string;
  quantidadeLinhas: number;
  quantidadeNcm: number;
  quantidadeNbs: number;
  quantidadeLc116: number;
  listaConflito: string[];
  // Convertido na primeira conferência e reaproveitado depois do Voltar.
  arquivoBase64: string | null;
};

/**
 * Importação da Tabela IBPT por planilha em 3 etapas (Enviar arquivos → Conferir → Concluir). Nada é gravado antes
 * do "Confirmar importação".
 */
@Component({
  selector: 'app-importar-planilha-ibpt-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatTooltipModule,
    MapaUfIbptComponent,
    SelectPesquisavelComponent,
  ],
  templateUrl: './importar-planilha-ibpt-dialog.component.html',
  styleUrl: './importar-planilha-ibpt-dialog.component.css',
})
export class ImportarPlanilhaIbptDialogComponent implements OnInit, OnDestroy {
  public etapaEnum = EtapaImportacao;
  public statusArquivoEnum = StatusArquivoImportacao;
  public comparacaoVersaoEnum = IbptComparacaoVersaoEnum;

  public readonly listaEtapa: { id: EtapaImportacao; descricao: string }[] = [
    { id: EtapaImportacao.EnviarArquivos, descricao: 'Enviar arquivos' },
    { id: EtapaImportacao.Conferir, descricao: 'Conferir' },
    { id: EtapaImportacao.Concluir, descricao: 'Concluir' },
  ];

  // Lookup de UF da API de Usuário, com o texto "Nome - UF" do select (como no cadastro de empresa). Vazio até
  // carregar.
  public listaUnidadeFederativa: UnidadeFederativa[] = [];

  public etapaAtual: EtapaImportacao = EtapaImportacao.EnviarArquivos;
  public nomeUsuario: string;

  // Etapa 1
  public listaArquivo: ArquivoImportacao[] = [];
  public isArrastando: boolean = false;
  public isConferindo: boolean = false;
  private ultimoIdArquivo: number = 0;

  // Etapa 2
  public validacao: ValidacaoImportacaoTabelaIbptResponse | null = null;
  public listaSiglaUfConferida: string[] = [];
  public datetimeConferencia: Date = new Date();
  public isConfirmado: boolean = false;
  public isGravando: boolean = false;
  // Andamento do "Gravando a tabela": envio dos arquivos (0 a 100) e depois gravação pela API, com tempo decorrido.
  public isEnviandoArquivos: boolean = false;
  public percentualEnvio: number = 0;
  public segundosGravando: number = 0;
  private intervaloGravacao: ReturnType<typeof setInterval> | null = null;
  public displayedColumnsRejeitada: string[] = ['nomeArquivo', 'numeroLinha', 'codigo', 'motivo'];
  public listaLinhaRejeitadaExibida: LinhaRejeitadaIbptStruct[] = [];
  private listaArquivoConferido: ArquivoPlanilhaIbptStruct[] = [];

  // Etapa 3
  public resultadoPorUf: Record<string, 'concluida' | 'parcial' | 'falhou'> = {};
  public versaoImportada: string = '';
  public datetimeConclusao: Date = new Date();
  public listaResultadoUf: ResultadoUfImportacaoIbptStruct[] = [];
  public displayedColumnsResultadoUf: string[] = [
    'nomeArquivo',
    'idUnidadeFederativa',
    'quantidadeLinhasValidas',
    'quantidadeLinhasRejeitadas',
    'resultado',
  ];
  public siglasUfImportadas: string = '';
  public quantidadeLinhasImportadas: number = 0;
  public quantidadeLinhasRejeitadasImportacao: number = 0;
  private isImportacaoConcluida: boolean = false;

  constructor(
    private dialogRef: MatDialogRef<ImportarPlanilhaIbptDialogComponent, ImportarPlanilhaIbptDialogResult>,
    private tabelaIbptService: TabelaIbptService,
    private authService: AuthService,
    private alertService: AlertService,
    private usuarioLookupService: UsuarioLookupService
  ) {
    this.nomeUsuario = this.authService.getUserInfos().nomeUsuarioAdmin;
  }

  ngOnInit(): void {
    this.usuarioLookupService.get().subscribe({
      next: (usuarioListaLookupsResponse: UsuarioListaLookupsResponse) => {
        if (usuarioListaLookupsResponse.isError) {
          this.alertService.show('Erro', usuarioListaLookupsResponse.errorDescription, AlertType.error);
          return;
        }

        // Exterior (EX) não tem tabela do IBPT: fica fora das opções de UF da importação.
        this.listaUnidadeFederativa = usuarioListaLookupsResponse.listaUnidadeFederativa
          .filter(uf => uf.uf !== 'EX')
          .map(uf => ({
            ...uf,
            descricaoExibicao: `${uf.nome} - ${uf.uf}`,
          }));
      },
      error: (error: any) => {
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  // Etapa 1: Enviar arquivos

  public escolherArquivos(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.adicionarArquivos(input.files);
    }
    // Permite escolher de novo o mesmo arquivo depois de removê-lo.
    input.value = '';
  }

  public arrastarSobre(event: DragEvent): void {
    event.preventDefault();
    this.isArrastando = true;
  }

  public soltarArquivos(event: DragEvent): void {
    event.preventDefault();
    this.isArrastando = false;
    if (event.dataTransfer?.files) {
      this.adicionarArquivos(event.dataTransfer.files);
    }
  }

  private adicionarArquivos(listaFile: FileList): void {
    Array.from(listaFile).forEach(file => {
      this.ultimoIdArquivo = this.ultimoIdArquivo + 1;
      const arquivo: ArquivoImportacao = {
        id: this.ultimoIdArquivo,
        arquivo: file,
        nomeArquivo: file.name,
        idStatus: StatusArquivoImportacao.Lendo,
        motivoRecusa: null,
        idUnidadeFederativaControl: new FormControl<number | null>(null),
        versao: '',
        quantidadeLinhas: 0,
        quantidadeNcm: 0,
        quantidadeNbs: 0,
        quantidadeLc116: 0,
        listaConflito: [],
        arquivoBase64: null,
      };
      this.listaArquivo.push(arquivo);
      this.lerArquivo(arquivo);
    });
  }

  // Lê só o necessário para a etapa 1: cabeçalho, versão da primeira linha e contagem por tipo (0 NCM, 1 NBS,
  // 2 LC 116). Windows-1252 cobre o ISO-8859-1 da história e o "…" que aparece no arquivo real.
  private lerArquivo(arquivo: ArquivoImportacao): void {
    if (!arquivo.nomeArquivo.toLowerCase().endsWith('.csv')) {
      this.recusarArquivo(arquivo, 'Recusado: o arquivo não é CSV.');
      return;
    }

    const leitor = new FileReader();
    leitor.onerror = () => this.recusarArquivo(arquivo, 'Recusado: não foi possível ler o arquivo.');
    leitor.onload = () => {
      let texto: string;
      try {
        texto = new TextDecoder('windows-1252').decode(leitor.result as ArrayBuffer);
      } catch {
        this.recusarArquivo(arquivo, 'Recusado: não foi possível ler o arquivo.');
        return;
      }

      const listaLinha = texto.split(/\r?\n/).filter(linha => linha.trim() !== '');
      if (listaLinha.length < 2) {
        this.recusarArquivo(arquivo, 'Recusado: arquivo vazio ou sem linhas de dados.');
        return;
      }

      const listaColuna = this.separarColunas(listaLinha[0]).map(coluna => coluna.trim().toLowerCase());
      if (!listaColuna.includes('codigo') || !listaColuna.includes('nacionalfederal')) {
        this.recusarArquivo(arquivo, 'Recusado: não é uma planilha do IBPT (cabeçalho não reconhecido).');
        return;
      }

      const indiceTipo = listaColuna.indexOf('tipo');
      const indiceVersao = listaColuna.indexOf('versao');
      const primeiraLinha = this.separarColunas(listaLinha[1]);
      arquivo.versao = indiceVersao >= 0 ? (primeiraLinha[indiceVersao] ?? '').trim() : '';
      arquivo.quantidadeLinhas = listaLinha.length - 1;
      for (let i = 1; i < listaLinha.length; i++) {
        const tipo = indiceTipo >= 0 ? (this.separarColunas(listaLinha[i])[indiceTipo] ?? '').trim() : '';
        if (tipo === '0') {
          arquivo.quantidadeNcm++;
        } else if (tipo === '1') {
          arquivo.quantidadeNbs++;
        } else if (tipo === '2') {
          arquivo.quantidadeLc116++;
        }
      }

      // Padrão do IBPT: TabelaIBPTax{UF}{versão}.csv (ex.: TabelaIBPTaxMG26.2.B.csv = MG). Fora do padrão ou sem
      // o lookup de UF, a UF fica vazia para o usuário escolher.
      const resultadoNome = /^TabelaIBPTax([A-Za-z]{2}).+\.csv$/i.exec(arquivo.nomeArquivo);
      const ufNome = resultadoNome
        ? this.listaUnidadeFederativa.find(uf => uf.uf === resultadoNome[1].toUpperCase())
        : undefined;
      arquivo.idUnidadeFederativaControl.setValue(ufNome ? ufNome.idUnidadeFederativa : null);
      arquivo.idStatus = StatusArquivoImportacao.Valido;
      this.atualizarConflitos();
    };
    leitor.readAsArrayBuffer(arquivo.arquivo);
  }

  // Separador ";" fora das aspas: a descrição vem entre aspas e pode ter ";" e vírgulas.
  private separarColunas(linha: string): string[] {
    const listaColuna: string[] = [];
    let coluna = '';
    let isEntreAspas = false;
    for (let i = 0; i < linha.length; i++) {
      const caractere = linha[i];
      if (caractere === '"') {
        if (isEntreAspas && linha[i + 1] === '"') {
          coluna += '"';
          i++;
        } else {
          isEntreAspas = !isEntreAspas;
        }
      } else if (caractere === ';' && !isEntreAspas) {
        listaColuna.push(coluna);
        coluna = '';
      } else {
        coluna += caractere;
      }
    }
    listaColuna.push(coluna);
    return listaColuna;
  }

  private recusarArquivo(arquivo: ArquivoImportacao, motivoRecusa: string): void {
    arquivo.idStatus = StatusArquivoImportacao.Recusado;
    arquivo.motivoRecusa = motivoRecusa;
    arquivo.listaConflito = [];
    this.atualizarConflitos();
  }

  public removerArquivo(arquivo: ArquivoImportacao): void {
    this.listaArquivo = this.listaArquivo.filter(a => a.id !== arquivo.id);
    this.atualizarConflitos();
  }

  public alterarUf(): void {
    // A UF vai no request junto do conteúdo; o base64 do arquivo continua valendo.
    this.atualizarConflitos();
  }

  // UF repetida: o aviso fica nos arquivos seguintes da mesma UF. Versão: comparada com a do primeiro arquivo válido.
  private atualizarConflitos(): void {
    const listaValido = this.listaArquivo.filter(a => a.idStatus === StatusArquivoImportacao.Valido);
    const versaoReferencia = listaValido.length > 0 ? listaValido[0].versao : '';
    listaValido.forEach((arquivo, indice) => {
      arquivo.listaConflito = [];
      const idUnidadeFederativa = arquivo.idUnidadeFederativaControl.value;
      if (idUnidadeFederativa !== null
        && listaValido.slice(0, indice).some(a => a.idUnidadeFederativaControl.value === idUnidadeFederativa)) {
        arquivo.listaConflito.push('Já existe um arquivo para a UF ' + this.siglaUf(idUnidadeFederativa) + '.');
      }

      if (arquivo.versao !== versaoReferencia) {
        arquivo.listaConflito.push('Versão ' + arquivo.versao + ' diferente dos demais arquivos (' + versaoReferencia + ').');
      }
    });
  }

  // Sigla da UF pelo id do lookup; "—" sem o lookup.
  public siglaUf(idUnidadeFederativa: number | null): string {
    return this.listaUnidadeFederativa.find(uf => uf.idUnidadeFederativa === idUnidadeFederativa)?.uf ?? '—';
  }

  // Motivo do "Conferir arquivos" desabilitado (tooltip); vazio quando pode conferir.
  public get motivoBloqueioConferir(): string {
    if (this.listaArquivo.some(a => a.idStatus === StatusArquivoImportacao.Lendo)) {
      return 'Aguarde a leitura dos arquivos.';
    }

    const listaValido = this.listaArquivo.filter(a => a.idStatus === StatusArquivoImportacao.Valido);
    if (listaValido.length === 0) {
      return 'Envie ao menos 1 arquivo válido.';
    }

    if (listaValido.some(a => a.idUnidadeFederativaControl.value === null)) {
      return 'Escolha a UF de cada arquivo.';
    }

    if (listaValido.some(a => a.listaConflito.length > 0)) {
      return 'Corrija os conflitos dos arquivos.';
    }

    return '';
  }

  // Só os arquivos válidos vão para a API; os recusados ficam na lista para o usuário ver o motivo.
  public conferir(): void {
    if (this.motivoBloqueioConferir || this.isConferindo) {
      return;
    }

    this.isConferindo = true;
    const listaValido = this.listaArquivo.filter(a => a.idStatus === StatusArquivoImportacao.Valido);
    Promise.all(listaValido.map(arquivo => this.converterParaBase64(arquivo)))
      .then(() => {
        const request: ImportacaoTabelaIbptRequest = {
          listaArquivo: listaValido.map(arquivo => ({
            nomeArquivo: arquivo.nomeArquivo,
            arquivoBase64: arquivo.arquivoBase64 as string,
            idUnidadeFederativa: arquivo.idUnidadeFederativaControl.value,
          })),
          // Só é lido no aplicar.
          isConfirmado: false,
        };
        this.validarImportacao(request, listaValido);
      })
      .catch(() => {
        this.isConferindo = false;
        this.alertService.show('Erro', 'Não foi possível ler os arquivos.', AlertType.error);
      });
  }

  // FileReader.readAsDataURL devolve "data:text/csv;base64,<conteúdo>"; a API recebe só o conteúdo.
  private converterParaBase64(arquivo: ArquivoImportacao): Promise<void> {
    if (arquivo.arquivoBase64) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const leitor = new FileReader();
      leitor.onerror = () => reject(leitor.error);
      leitor.onload = () => {
        const dataUrl = leitor.result as string;
        arquivo.arquivoBase64 = dataUrl.substring(dataUrl.indexOf(',') + 1);
        resolve();
      };
      leitor.readAsDataURL(arquivo.arquivo);
    });
  }

  private validarImportacao(request: ImportacaoTabelaIbptRequest, listaValido: ArquivoImportacao[]): void {
    this.tabelaIbptService.validarImportacao(request).subscribe({
      next: (validarImportacaoIbptResponse: ValidacaoImportacaoTabelaIbptResponse) => {
        this.isConferindo = false;
        // A leitura do navegador repete as recusas de arquivo da API; se a API ainda recusar algum, ele fica
        // recusado na etapa 1 com o motivo devolvido e os demais seguem na próxima conferência. A lista volta
        // preenchida também com erro (conflito de UF ou de versão).
        const listaRecusado = (validarImportacaoIbptResponse.listaArquivo ?? []).filter(a => !a.isAceito);
        listaRecusado.forEach(arquivoRecusado => {
          const arquivo = listaValido.find(a => a.nomeArquivo === arquivoRecusado.nomeArquivo);
          if (arquivo && arquivoRecusado.motivoRecusa) {
            this.recusarArquivo(arquivo, arquivoRecusado.motivoRecusa);
          }
        });

        if (validarImportacaoIbptResponse.isError) {
          this.alertService.show('Erro', validarImportacaoIbptResponse.errorDescription, AlertType.error);
          return;
        }

        if (listaRecusado.length > 0) {
          return;
        }

        this.validacao = validarImportacaoIbptResponse;
        this.listaArquivoConferido = request.listaArquivo;
        this.listaSiglaUfConferida = validarImportacaoIbptResponse.listaArquivo.map(a => this.siglaUf(a.idUnidadeFederativa));
        this.listaLinhaRejeitadaExibida = validarImportacaoIbptResponse.listaLinhaRejeitada.slice(0, 10);
        this.isConfirmado = false;
        this.datetimeConferencia = new Date();
        this.etapaAtual = EtapaImportacao.Conferir;
      },
      error: (error: any) => {
        this.isConferindo = false;
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  // Etapa 2: Conferir

  public verMaisRejeitadas(): void {
    if (!this.validacao) {
      return;
    }

    this.listaLinhaRejeitadaExibida = this.validacao.listaLinhaRejeitada
      .slice(0, this.listaLinhaRejeitadaExibida.length + 10);
  }

  public get isUltimaPaginaRejeitada(): boolean {
    return !this.validacao || this.listaLinhaRejeitadaExibida.length >= this.validacao.listaLinhaRejeitada.length;
  }

  // CSV gerado no navegador com a lista completa devolvida pela conferência.
  public baixarListaCompleta(): void {
    if (!this.validacao) {
      return;
    }

    const listaLinhaCsv = ['Arquivo;Linha;Código;Motivo'];
    this.validacao.listaLinhaRejeitada.forEach(linha => {
      listaLinhaCsv.push([linha.nomeArquivo, linha.numeroLinha.toString(), linha.codigo ?? '', linha.motivo]
        .map(valor => '"' + valor.replace(/"/g, '""') + '"')
        .join(';'));
    });

    // BOM para o Excel abrir em UTF-8.
    const blob = new Blob(['﻿' + listaLinhaCsv.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'linhas-rejeitadas-ibpt.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  public get isPodeConfirmar(): boolean {
    return !!this.validacao && (!this.validacao.isConfirmacaoObrigatoria || this.isConfirmado);
  }

  public get mensagemComparacao(): string {
    if (!this.validacao) {
      return '';
    }

    const versao = this.validacao.versao ?? '';
    const versaoEmUso = this.validacao.versaoEmUso ?? '';
    switch (this.validacao.ibptComparacaoVersao) {
      case IbptComparacaoVersaoEnum.Igual:
        return 'A versão ' + versao + ' já está em uso. Os itens existentes serão atualizados e nenhum será duplicado.';
      case IbptComparacaoVersaoEnum.Posterior:
        return 'A versão ' + versao + ' é mais nova que a em uso (' + versaoEmUso
          + '). Ao confirmar, ela passa a ser a versão vigente.';
      case IbptComparacaoVersaoEnum.Anterior:
        return 'A versão ' + versao + ' é mais antiga que a em uso (' + versaoEmUso
          + '). As alíquotas atuais serão substituídas por valores anteriores.';
      case IbptComparacaoVersaoEnum.MesmoInicioDiferente:
        return 'A versão ' + versao + ' (' + this.formatarData(this.validacao.dataVigenciaInicio) + ' a '
          + this.formatarData(this.validacao.dataVigenciaFim) + ') tem o mesmo início de vigência da versão em uso ('
          + versaoEmUso + ', ' + this.formatarData(this.validacao.dataVigenciaInicioEmUso) + ' a '
          + this.formatarData(this.validacao.dataVigenciaFimEmUso) + '). Pode ser uma retificação ou uma versão anterior.';
      default:
        return '';
    }
  }

  // Aviso amarelo: versão mais antiga ou mesmo início de vigência com versão ou fim diferente.
  public get isAvisoComparacao(): boolean {
    return this.validacao?.ibptComparacaoVersao === IbptComparacaoVersaoEnum.Anterior
      || this.validacao?.ibptComparacaoVersao === IbptComparacaoVersaoEnum.MesmoInicioDiferente;
  }

  public get textoConfirmacao(): string {
    return this.validacao?.ibptComparacaoVersao === IbptComparacaoVersaoEnum.MesmoInicioDiferente
      ? 'Confirmo que esta planilha é a versão correta'
      : 'Entendo e quero importar a versão mais antiga';
  }

  public voltar(): void {
    this.validacao = null;
    this.etapaAtual = EtapaImportacao.EnviarArquivos;
  }

  public confirmarImportacao(): void {
    if (!this.isPodeConfirmar || this.isGravando) {
      return;
    }

    const request: ImportacaoTabelaIbptRequest = {
      listaArquivo: this.listaArquivoConferido,
      isConfirmado: this.isConfirmado,
    };
    this.isGravando = true;
    this.dialogRef.disableClose = true;
    this.isEnviandoArquivos = false;
    this.percentualEnvio = 0;
    this.iniciarContagem();
    this.tabelaIbptService.aplicarImportacao(request).subscribe({
      next: (evento: HttpEvent<AplicacaoImportacaoTabelaIbptResponse>) => {
        // Só mostra "Enviando" quando o navegador informa o progresso do envio (o fetch não informa): sem ele,
        // o bloqueio fica direto em "Gravando", que é o que de fato se sabe.
        if (evento.type === HttpEventType.UploadProgress && evento.total) {
          this.percentualEnvio = Math.floor((evento.loaded / evento.total) * 100);
          if (this.percentualEnvio < 100) {
            this.isEnviandoArquivos = true;
          } else if (this.isEnviandoArquivos) {
            // Envio terminou: a API passa a gravar e o tempo decorrido recomeça.
            this.isEnviandoArquivos = false;
            this.iniciarContagem();
          }
          return;
        }

        if (evento.type !== HttpEventType.Response) {
          return;
        }

        this.finalizarGravacao();
        const aplicarImportacaoIbptResponse = evento.body as AplicacaoImportacaoTabelaIbptResponse;
        if (aplicarImportacaoIbptResponse.isError) {
          this.alertService.show('Erro', aplicarImportacaoIbptResponse.errorDescription, AlertType.error);
          return;
        }

        // Concluída = nenhuma linha rejeitada; parcial = válidas e rejeitadas; falhou = nenhuma válida.
        const listaResultadoUf = aplicarImportacaoIbptResponse.listaResultadoUf;
        this.resultadoPorUf = {};
        // O mapa e a tabela leem o resultado pela sigla do lookup.
        listaResultadoUf.forEach(resultadoUf => {
          const sigla = this.siglaUf(resultadoUf.idUnidadeFederativa);
          if (resultadoUf.quantidadeLinhasValidas === 0) {
            this.resultadoPorUf[sigla] = 'falhou';
          } else if (resultadoUf.quantidadeLinhasRejeitadas === 0) {
            this.resultadoPorUf[sigla] = 'concluida';
          } else {
            this.resultadoPorUf[sigla] = 'parcial';
          }
        });
        this.versaoImportada = aplicarImportacaoIbptResponse.versao ?? '';
        this.datetimeConclusao = this.lerDataUtc(aplicarImportacaoIbptResponse.datetimeFim) ?? new Date();
        this.listaResultadoUf = listaResultadoUf;
        this.siglasUfImportadas = listaResultadoUf.map(r => this.siglaUf(r.idUnidadeFederativa)).join(', ');
        this.quantidadeLinhasImportadas = listaResultadoUf.reduce((total, r) => total + r.quantidadeLinhasValidas, 0);
        this.quantidadeLinhasRejeitadasImportacao = listaResultadoUf
          .reduce((total, r) => total + r.quantidadeLinhasRejeitadas, 0);
        this.isImportacaoConcluida = true;
        this.etapaAtual = EtapaImportacao.Concluir;
      },
      error: (error: any) => {
        this.finalizarGravacao();
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  private iniciarContagem(): void {
    this.pararContagemGravacao();
    this.segundosGravando = 0;
    this.intervaloGravacao = setInterval(() => this.segundosGravando++, 1000);
  }

  private finalizarGravacao(): void {
    this.pararContagemGravacao();
    this.isGravando = false;
    this.isEnviandoArquivos = false;
  }

  private pararContagemGravacao(): void {
    if (this.intervaloGravacao !== null) {
      clearInterval(this.intervaloGravacao);
      this.intervaloGravacao = null;
    }
  }

  public get textoTempoGravacao(): string {
    const minutos = Math.floor(this.segundosGravando / 60);
    const segundos = this.segundosGravando % 60;
    return 'há ' + (minutos > 0 ? minutos + ' min ' : '') + segundos + ' s';
  }

  public get textoGravacao(): string {
    const quantidadeLinhas = (this.validacao?.quantidadeLinhasValidas ?? 0).toLocaleString('pt-BR');
    const quantidadeUf = this.listaArquivoConferido.length;
    return 'Gravando ' + quantidadeLinhas + ' linhas de ' + quantidadeUf + (quantidadeUf === 1 ? ' UF…' : ' UFs…');
  }

  public ngOnDestroy(): void {
    this.pararContagemGravacao();
  }

  // Etapa 3: Concluir

  public importarOutrosArquivos(): void {
    this.listaArquivo = [];
    this.validacao = null;
    this.listaArquivoConferido = [];
    this.etapaAtual = EtapaImportacao.EnviarArquivos;
  }

  public verNoHistorico(): void {
    this.dialogRef.close({ isConcluido: true, isVerHistorico: true });
  }

  public cancelar(): void {
    this.dialogRef.close({ isConcluido: this.isImportacaoConcluida, isVerHistorico: false });
  }

  // Vigência chega como aaaa-mm-dd: lida como texto para não voltar um dia no fuso de Brasília.
  public formatarData(data: string | null): string {
    if (!data || data.length < 10) {
      return '—';
    }

    return data.substring(8, 10) + '/' + data.substring(5, 7) + '/' + data.substring(0, 4);
  }

  // Mesma regra da tela Tabela IBPT: a API devolve o horário em UTC, nem sempre com o "Z" no fim.
  private lerDataUtc(valor: Date | string | null | undefined): Date | null {
    if (!valor) {
      return null;
    }

    if (valor instanceof Date) {
      return valor;
    }

    const texto = valor.toString();
    return new Date(/(Z|[+-]\d{2}:\d{2})$/.test(texto) ? texto : texto + 'Z');
  }
}
